/**
 * Which picture goes in which frame, and how it sits there - "Fill my card"
 * (2026-10-11; made smarter 2026-10-17, when the owner asked for it to be
 * "super smart and creative", as most people will use it). Pure geometry
 * over what faces.ts and look.ts found on the device:
 *
 *  - every picture is scored against every frame, and the best arrangement
 *    kept: the picture with the most and biggest faces to the big circle (the
 *    card's big reveal) and the front, faces to roomy frames and never to a
 *    sliver that would cut them, pictures without faces (landscapes) to the
 *    slim frames, wide pictures to wide frames and tall ones to tall frames,
 *    a picture too small for a big frame kept out of it, and behind the
 *    message the calmest picture without faces, where words read best;
 *  - a picture is used once before any is used again, and never twice where
 *    the two frames can be seen at the same time (any view showing both);
 *  - the person's own photographs before pictures from the library, which go
 *    behind the message;
 *  - first the frames in order of importance take their best picture, then
 *    pairs are swapped wherever that makes the whole card better;
 *  - "another arrangement" scores with a little chance mixed in (a seed), and
 *    away from what each frame has now, so each press gives a new card that
 *    is still a good one;
 *  - a face is moved to the middle of the part of its frame that shows - never
 *    under another shape that lies over it when the card is shut, like the
 *    letterfold's round seal - and zoomed to a good size - a
 *    little under half the frame across, a group most of it - and never so
 *    far that blank paper would show: it stops short instead (limitTransform,
 *    'fill'). A picture without faces keeps the default fit, which already
 *    aims where a face usually is (placement.ts DEFAULT_FOCUS).
 */
import {
  DEFAULT_TRANSFORM, MAX_SCALE, apply, coverPlacement, frameOutlines, invert, limitTransform, zoneFrame,
  type ImageRef, type ImageTransform, type ProjectDocument, type Pt, type TemplateDocument, type TemplateZone,
} from '@foldmycard/scene';
import type { FaceBox } from './faces.ts';
import type { PictureLook } from './look.ts';

export interface Candidate {
  ref: ImageRef;
  /** The size the card draws it at (its preview copy). */
  width: number;
  height: number;
  faces: FaceBox[];
  /** Already somewhere on this card (outside the frames being filled): used again only once the others are. */
  used: boolean;
  /** How light and how busy it is (look.ts), when measured. */
  look?: PictureLook;
  /** A drawing or photograph from the picture library (a theme's), not one of the person's own. */
  library?: boolean;
}
export interface Planned { zone: number; ref: ImageRef; transform: ImageTransform | null }
export interface FillOptions {
  /** 'empty': the empty frames only; 'all': every frame not left plain - a whole new arrangement. */
  mode?: 'empty' | 'all';
  /** 0: the best arrangement; any other number: another good one. */
  seed?: number;
}

const area = (p: Pt[]) => Math.abs(p.reduce((s, a, i) => {
  const b = p[(i + 1) % p.length];
  return s + a[0] * b[1] - a[1] * b[0];
}, 0)) / 2;
const perimeter = (p: Pt[]) => p.reduce((s, a, i) => {
  const b = p[(i + 1) % p.length];
  return s + Math.hypot(b[0] - a[0], b[1] - a[1]);
}, 0);
const dot = (p: Pt, q: Pt) => p[0] * q[0] + p[1] * q[1];

/** The room a zone has for a face: the radius of the biggest circle in it, near enough (2 x area / perimeter). */
export const room = (z: TemplateZone) => (2 * area(z.outline)) / perimeter(z.outline);

/** The faces that matter - the biggest, and any at least a third its size - as one box; null if none. */
function subject(faces: FaceBox[]): { box: FaceBox; many: boolean; count: number } | null {
  if (!faces.length) return null;
  const big = faces[0].w * faces[0].h;
  const main = faces.filter((f) => f.w * f.h >= big / 3);
  const x0 = Math.min(...main.map((f) => f.x)), y0 = Math.min(...main.map((f) => f.y));
  const x1 = Math.max(...main.map((f) => f.x + f.w)), y1 = Math.max(...main.map((f) => f.y + f.h));
  return { box: { x: x0, y: y0, w: x1 - x0, h: y1 - y0, score: faces[0].score }, many: main.length > 1, count: main.length };
}

const insidePoly = ([x, y]: Pt, poly: Pt[]) => {
  let hit = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i], [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
};

/** Where a face goes in a zone, on the page, and the room it has there. */
export interface FaceSpot { at: Pt; room: number }

/**
 * Where a face should go in a zone that another shape partly covers when the
 * card is first opened to it (2026-10-17: the letterfold's round seal sat on the
 * face of the front's big photo): the middle of the biggest part left showing,
 * and the room there - on the page, in mm. Null when nothing covers the zone:
 * then its own middle is right.
 */
export function faceSpot(t: TemplateDocument, z: TemplateZone): FaceSpot | null {
  const view = t.views.find((v) => v.id === z.firstSeen);
  const mine = view?.pieces.filter((pc) => pc.zone === z.number) ?? [];
  if (!view || !mine.length) return null;
  const order = new Map(t.zones.map((x) => [x.number, x.drawOrder]));
  const piece = mine.reduce((a, b) => (area(b.outline) > area(a.outline) ? b : a));
  const xs = piece.outline.map((p) => p[0]), ys = piece.outline.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const box = (o: Pt[]) => o.some(([x, y]) => x >= x0 && x <= x1 && y >= y0 && y <= y1);
  const over = view.pieces.filter((pc) => pc.zone !== z.number && (order.get(pc.zone) ?? 0) > z.drawOrder && box(pc.outline));
  // a grid over the piece: which points show, and of those the one farthest from anything that does not
  if (!over.length) return null;
  const N = 48, dx = (x1 - x0) / N, dy = (y1 - y0) / N;
  const free: Pt[] = [], blocked: Pt[] = [];
  let covered = 0;
  for (let i = 0; i <= N; i++) for (let j = 0; j <= N; j++) {
    const p: Pt = [x0 + i * dx, y0 + j * dy];
    const inside = insidePoly(p, piece.outline), hidden = inside && over.some((pc) => insidePoly(p, pc.outline));
    if (hidden) covered++;
    (inside && !hidden ? free : blocked).push(p);
  }
  // nothing actually lies over it, or nothing of it shows
  if (!covered || !free.length) return null;
  // how far each showing point is from the frame's own edge, and from the nearest hidden point
  const edge = (p: Pt) => piece.outline.reduce((d, a, i) => {
    const c = piece.outline[(i + 1) % piece.outline.length];
    const [ex, ey] = [c[0] - a[0], c[1] - a[1]];
    const k = Math.max(0, Math.min(1, ((p[0] - a[0]) * ex + (p[1] - a[1]) * ey) / (ex * ex + ey * ey || 1)));
    return Math.min(d, Math.hypot(p[0] - a[0] - k * ex, p[1] - a[1] - k * ey));
  }, Infinity);
  let best: Pt = free[0], far = -1;
  for (const p of free) {
    let d = edge(p);
    for (const q of blocked) if (insidePoly(q, piece.outline)) d = Math.min(d, Math.hypot(p[0] - q[0], p[1] - q[1]));
    if (d > far) { far = d; best = p; }
  }
  // a fold moves the paper without stretching it: view millimetres are page millimetres
  return { at: apply(invert(piece.pageToView), best), room: far };
}

/**
 * The move and zoom that put a picture's faces in the middle of a zone - or at
 * `spot`, the middle of the part of it that shows - at a good size, with no
 * blank paper; null when it has no faces.
 */
export function frameFaces(z: TemplateZone, img: { width: number; height: number }, faces: FaceBox[], spot: FaceSpot | null = null): ImageTransform | null {
  const s = subject(faces);
  if (!s) return null;
  const outlines = frameOutlines(z);
  const f = zoneFrame(outlines, z.up);
  const base = coverPlacement(outlines, z.up, img);
  const mmPerPx = Math.hypot(base[0], base[1]);
  const faceMm = Math.max(s.box.w * img.width, s.box.h * img.height) * mmPerPx;
  // the box runs from brow to chin: the head around it is half as big again
  const roomHere = spot ? Math.min(room(z), spot.room) : room(z);
  const want = roomHere * (s.many ? 1.5 : 0.9);
  const centre = apply(base, [(s.box.x + s.box.w / 2) * img.width, (s.box.y + s.box.h / 2) * img.height]);
  const [px, py] = f.pivot;
  const [tx, ty] = spot?.at ?? f.pivot;
  let best: ImageTransform | null = null, bestMiss = Infinity;
  // the right size first; where the zone's edges hold the face off-centre, a
  // little more zoom gives it room to come to the middle
  let scale = Math.min(3, Math.max(1, want / faceMm));
  for (let i = 0; i < 6; i++, scale = Math.min(MAX_SCALE, scale * 1.18)) {
    // zooming about the pivot puts the face at pivot + scale * (centre - pivot): move it from there to the spot
    const shift: Pt = [tx - px - scale * (centre[0] - px), ty - py - scale * (centre[1] - py)];
    const to: ImageTransform = { ...DEFAULT_TRANSFORM, scale, offsetX: dot(shift, f.right) / f.width, offsetY: dot(shift, f.down) / f.height };
    const got = limitTransform(outlines, z.up, img, { ...DEFAULT_TRANSFORM, scale }, to, 'fill');
    const miss = Math.hypot((got.offsetX - to.offsetX) * f.width, (got.offsetY - to.offsetY) * f.height);
    if (miss < bestMiss - 0.5) { best = got; bestMiss = miss; }
    if (miss < 0.1 * roomHere) break;
  }
  return best;
}

/** A small repeatable random sequence, so a seed always gives the same arrangement. */
function seeded(seed: number) {
  let s = (seed * 2654435761) >>> 0 || 1;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}

interface Frame { z: TemplateZone; room: number; aspect: number; importance: number; message: boolean; views: Set<string>; minMm: number }
interface Pic { c: Candidate; aspect: number; face: number; count: number; px: number }

/**
 * Pictures for the card's frames (never those left plain): the empty ones, or
 * with mode 'all' every one. Returns the frames it filled, with the picture and
 * how it sits; the frames it leaves alone are not in the list.
 */
export function planFill(template: TemplateDocument, doc: ProjectDocument, pictures: Candidate[], opts: FillOptions = {}): Planned[] {
  const mode = opts.mode ?? 'empty', seed = opts.seed ?? 0;
  // frames for words only (the fortune teller's numbers and fortunes) or for a symbol (the square
  // letter's points) take no picture
  const zones = template.zones.filter((z) => !z.textOnly && !z.symbolOnly && !doc.zones[z.number]?.plain && (mode === 'all' || !doc.zones[z.number]?.image));
  if (!zones.length || !pictures.length) return [];

  const maxRoom = Math.max(...template.zones.map(room));
  /** The views each zone shows in: two frames seen together must not show the same picture. */
  const seenIn = new Map<number, Set<string>>();
  for (const v of template.views) for (const pc of v.pieces) {
    if (!seenIn.has(pc.zone)) seenIn.set(pc.zone, new Set());
    seenIn.get(pc.zone)!.add(v.id);
  }
  const together = (a: Frame, b: Frame) => [...a.views].some((v) => b.views.has(v));
  /** Pictures already in frames that stay as they are, and where those frames are seen. */
  const filling = new Set(zones.map((z) => z.number));
  const kept = template.zones.filter((z) => !filling.has(z.number) && doc.zones[z.number]?.image && !doc.zones[z.number]?.plain)
    .map((z) => ({ id: doc.zones[z.number]!.image!.id, views: seenIn.get(z.number) ?? new Set<string>() }));
  /** Whether a picture is already in a kept frame seen together with this one. */
  const besideKept = (f: Frame, id: string) => kept.some((k) => k.id === id && [...k.views].some((v) => f.views.has(v)));
  const maxArea = Math.max(...template.zones.map((z) => area(z.outline)));
  const frames: Frame[] = zones.map((z) => {
    const f = zoneFrame(frameOutlines(z), z.up);
    const message = z.defaultContent === 'text';
    // the big reveal first, then the front of the card, then by size
    const importance = area(z.outline) / maxArea + (z.kind === 'circle' ? 1.2 : 0) + (z.firstSeen === 'f1' ? 0.35 : 0) - (message ? 0.6 : 0);
    return { z, room: room(z) / maxRoom, aspect: f.width / f.height, importance, message, views: seenIn.get(z.number) ?? new Set(), minMm: Math.min(f.width, f.height) };
  }).sort((a, b) => b.importance - a.importance);

  const pics: Pic[] = pictures.map((c) => {
    const s = subject(c.faces);
    return { c, aspect: c.width / c.height, face: s ? Math.sqrt(s.box.w * s.box.h) : 0, count: s?.count ?? 0, px: Math.min(c.width, c.height) };
  });
  const rnd = seeded(seed);
  const jitter = new Map<string, number>();
  const noise = (f: Frame, p: Pic) => {
    if (!seed) return 0;
    const k = `${f.z.number}:${p.c.ref.id}`;
    if (!jitter.has(k)) jitter.set(k, (rnd() - 0.5) * 0.9);
    return jitter.get(k)!;
  };

  /** How well a picture suits a frame, on its own; higher is better. */
  const fit = (f: Frame, p: Pic) => {
    let s = 1.2 * Math.min(f.aspect / p.aspect, p.aspect / f.aspect);        // little cropped away
    if (f.message) {
      // behind the words: no faces, and calm
      s += p.count ? -2 : 1;
      if (p.c.look) s += 1.5 * Math.max(0, 1 - p.c.look.busy / 0.15);
    } else if (p.count) {
      s += 1.6 * Math.min(1, p.face * 3) * f.room;                           // faces to roomy frames
      if (p.count > 1) s += 0.9 * f.room * f.room;                           // a group to the biggest
      if (f.z.kind === 'circle') s += 0.6 * Math.min(1, p.face * 3) + 0.25 * Math.min(p.count, 4);
      if (f.room < 0.35) s -= 1.2;                                           // a face in a sliver is cut
    } else {
      s += 0.9 * (1 - f.room);                                               // landscapes to the slim frames
    }
    // the person's own photos first - and always on the front and in the big circle
    if (p.c.library && !f.message) s -= f.z.kind === 'circle' || f.z.firstSeen === 'f1' ? 3 : 0.7;
    // sharp enough: about 6 pixels a millimetre (150 dots an inch) across the frame's narrow side
    const ppm = p.px / Math.max(1, f.minMm * 1.2);
    if (ppm < 6) s -= (6 - ppm) / 6;
    if (p.c.used) s -= 0.8;
    // "another arrangement": away from what is there now
    if (mode === 'all' && seed && doc.zones[f.z.number]?.image?.id === p.c.ref.id) s -= 1.1;
    return s + noise(f, p);
  };

  // the frames in order of importance, each taking its best picture - fresh pictures before repeats,
  // and never one already in the same view
  const plan = new Map<Frame, Pic>();
  const uses = (p: Pic) => [...plan.values()].filter((q) => q === p).length;
  const inView = (f: Frame, p: Pic) => [...plan].some(([g, q]) => q === p && together(g, f));
  const score = (f: Frame, p: Pic) => fit(f, p) * (f.message ? 1 : 1 + 0.6 * f.importance) - 3 * uses(p)
    - (inView(f, p) || besideKept(f, p.c.ref.id) ? 4 : 0);
  for (const f of frames) {
    let best: Pic | null = null, bestScore = -Infinity;
    for (const p of pics) {
      const sc = score(f, p);
      if (sc > bestScore) { best = p; bestScore = sc; }
    }
    if (best) plan.set(f, best);
  }

  // then swap any two frames' pictures, or bring in a picture not yet used, wherever the card gets better
  const total = () => [...plan].reduce((s, [f, p]) => s + fit(f, p) * (f.message ? 1 : 1 + 0.6 * f.importance), 0)
    - [...new Set(plan.values())].reduce((s, p) => s + 3 * Math.max(0, uses(p) - 1), 0)
    - frames.reduce((s, f) => s + ([...plan].filter(([g, q]) => g !== f && together(g, f) && q === plan.get(f)).length ? 4 : 0), 0)
    - frames.reduce((s, f) => s + (besideKept(f, plan.get(f)!.c.ref.id) ? 4 : 0), 0);
  let now = total();
  for (let pass = 0; pass < 4; pass++) {
    let better = false;
    for (let i = 0; i < frames.length; i++) {
      for (let j = i + 1; j < frames.length; j++) {
        const a = plan.get(frames[i])!, b = plan.get(frames[j])!;
        if (a === b) continue;
        plan.set(frames[i], b); plan.set(frames[j], a);
        const next = total();
        if (next > now + 1e-6) { now = next; better = true; } else { plan.set(frames[i], a); plan.set(frames[j], b); }
      }
      for (const p of pics) {
        if ([...plan.values()].includes(p)) continue;
        const was = plan.get(frames[i])!;
        plan.set(frames[i], p);
        const next = total();
        if (next > now + 1e-6) { now = next; better = true; } else plan.set(frames[i], was);
      }
    }
    if (!better) break;
  }

  return [...plan].map(([f, p]) => ({ zone: f.z.number, ref: p.c.ref,
    transform: f.message ? null : frameFaces(f.z, p.c, p.c.faces, p.count ? faceSpot(template, f.z) : null) }));
}
